import React from 'react';
import { routes } from 'config';
import { Link } from '@perch-labs/shrike';
import { useQuery } from '@apollo/client';
import { NotificationKey } from 'domain/notifications';
import { LoadingPage } from 'components/loading';
import {
  mapLocation,
  VNM_TERMS_MODAL,
  shouldDismissOnClick,
  UTILITY_SYNC_POP_UP,
  TURN_OFF_AUTO_PAY,
  INVALID_PROOF_DOC,
} from '../notification.utils';
import { ButtonLinkProps } from '../notification.component';
import { GET_UTILITY_SYNC_CREDENTIAL_DATA } from '../../../pages/settings/secondary/utility-sync/utility-sync-credential/utility-sync-credential.api';

interface Props {
  buttonLinkProps?: ButtonLinkProps;
  notification: QueriedNotification;
  dismissNotification: () => void;
}

const CHECK_LMI_ELIGIBILITY = '/settings/check-lmi-eligibility';

export const Action = ({
  notification,
  buttonLinkProps,
  dismissNotification,
}: Props) => {
  const { actionType, displayValues, notifiableId, notificationKey, targetId } =
    notification;
  const { buttonText } = displayValues;
  const utilityAccountId =
    (notificationKey === NotificationKey.advocacyCampaignAvailable ||
      notificationKey === NotificationKey.invalidProofDoc) &&
    notifiableId
      ? notifiableId
      : undefined;
  const utilityAccountLMISourceTypeId =
    notificationKey === NotificationKey.invalidProofDoc && targetId
      ? targetId
      : undefined;
  const utilityCredentialId = targetId || undefined;
  const location = mapLocation(actionType, utilityAccountId);
  const marginProps = { margin: { top: '16px' } };
  const { utilitySyncCredential } = routes({
    utilityAccountId,
    utilityCredentialId,
  });

  const result = useQuery<
    GetUtilitySyncCredentialDataQuery,
    GetUtilitySyncCredentialDataQueryVariables
  >(GET_UTILITY_SYNC_CREDENTIAL_DATA, {
    skip: !utilityCredentialId || location !== TURN_OFF_AUTO_PAY,
    variables: { utilityCredentialId: Number(utilityCredentialId) || -1 },
  });

  const { data, loading } = result;
  if (loading) return <LoadingPage />;

  const createLinkTo = (path: string) => ({
    pathname: path,
    search: `?utilityAccountId=${utilityAccountId}&utilityAccountLMISourceTypeId=${utilityAccountLMISourceTypeId}`,
  });

  switch (location) {
    case TURN_OFF_AUTO_PAY: {
      if (data) {
        const { utilityCredential }: GetUtilitySyncCredentialDataQuery = data;
        const { utility } = utilityCredential;
        return (
          <Link
            size="small"
            type="CTA"
            color="primaryInverse"
            href={utility.url || ''}
            onClick={() =>
              shouldDismissOnClick(actionType)
                ? dismissNotification()
                : undefined
            }
            {...marginProps}
            {...buttonLinkProps}
          >
            {buttonText}
          </Link>
        );
      } else {
        return null;
      }
    }
    case VNM_TERMS_MODAL: {
      const { contracts } = routes();
      return (
        <Link
          size="small"
          type="CTA"
          color="primaryInverse"
          to={{
            pathname: contracts,
          }}
          {...marginProps}
          {...buttonLinkProps}
        >
          {buttonText}
        </Link>
      );
    }
    case UTILITY_SYNC_POP_UP: {
      return (
        <Link
          size="small"
          type="CTA"
          color="primaryInverse"
          to={{
            pathname: utilitySyncCredential,
            state: { showUtilityResyncPopup: true },
          }}
          onClick={() =>
            shouldDismissOnClick(actionType) ? dismissNotification() : undefined
          }
          {...marginProps}
          {...buttonLinkProps}
        >
          {buttonText}
        </Link>
      );
    }
    case CHECK_LMI_ELIGIBILITY: {
      const isInvalidProofDocKey = notificationKey === INVALID_PROOF_DOC;
      const linkTo = isInvalidProofDocKey
        ? createLinkTo(CHECK_LMI_ELIGIBILITY)
        : { pathname: CHECK_LMI_ELIGIBILITY };

      return (
        <Link
          size="small"
          type="CTA"
          color={isInvalidProofDocKey ? 'primaryInverse' : 'primary'}
          backgroundless={!isInvalidProofDocKey}
          to={linkTo}
          onClick={() =>
            shouldDismissOnClick(actionType) ? dismissNotification() : undefined
          }
          {...marginProps}
          {...buttonLinkProps}
        >
          {buttonText}
        </Link>
      );
    }
    case undefined:
      return null;
    default:
      return (
        <Link
          size="small"
          type="CTA"
          color="primaryInverse"
          to={location}
          onClick={() =>
            shouldDismissOnClick(actionType) ? dismissNotification() : undefined
          }
          {...marginProps}
          {...buttonLinkProps}
        >
          {buttonText}
        </Link>
      );
  }
};
